/**
 * PURPOSE: Administrar publicaciones individuales de información y resultados en Winscore. Lo publicado en
 *   "Información" (tipo 1) también se publica automáticamente en la Información de Hayllis 2.0 (FTR-0037).
 * RESPONSIBILITIES: Permitir publicar PDF o enlace, listar el origen de cada registro y aplicar eliminación lógica.
 * COLLABORATORS: individualDocumentService.js, FasesArea.jsx y documentosinfo.
 * ROUTE: winscore/src/area/FasesArea/SportIndividual/Area.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FaExternalLinkAlt,
  FaFilePdf,
  FaLink,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import {
  createIndividualDocument,
  deleteIndividualDocument,
  listIndividualDocuments,
} from "../../../services/individualDocumentService.js";

const PUBLICATION_TYPE = Object.freeze({
  PROGRAMACION: 1,
  RESULTADO: 2,
});

const SOURCE_TYPE = Object.freeze({
  PDF: "pdf",
  LINK: "link",
});

const isHttpUrl = (value) => {
  try {
    const parsed = new URL(String(value || "").trim());
    return ["http:", "https:"].includes(parsed.protocol);
  } catch (_error) {
    return false;
  }
};

const resolveDocumentSource = (document) => {
  if (Number(document?.tipodoc) === 2) return SOURCE_TYPE.LINK;
  if (Number(document?.tipodoc) === 1) return SOURCE_TYPE.PDF;

  const path = String(document?.path || "").toLowerCase();
  return path.includes(".pdf") ? SOURCE_TYPE.PDF : SOURCE_TYPE.LINK;
};

function SourceBadge({ source }) {
  const isPdf = source === SOURCE_TYPE.PDF;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        isPdf
          ? "bg-red-50 text-red-700"
          : "bg-blue-50 text-blue-700"
      }`}
    >
      {isPdf ? <FaFilePdf /> : <FaLink />}
      {isPdf ? "PDF" : "Enlace"}
    </span>
  );
}

function PublicationTable({ title, documents, onDelete, deletingId }) {
  return (
    <section className="overflow-hidden rounded-2xl bg-white p-5 shadow-lg sm:p-6">
      <h3 className="mb-4 text-xl font-semibold text-indigo-700">{title}</h3>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                Título
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                Origen
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                Acción
              </th>
            </tr>
          </thead>
          <tbody>
            {documents.length ? (
              documents.map((document) => {
                const source = resolveDocumentSource(document);
                return (
                  <tr
                    key={document.iddoc}
                    className="border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                      {document.title || document.filename || "Publicación"}
                    </td>
                    <td className="px-4 py-3">
                      <SourceBadge source={source} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <a
                          href={document.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
                        >
                          <FaExternalLinkAlt /> Abrir
                        </a>
                        <button
                          type="button"
                          onClick={() => onDelete(document.iddoc)}
                          disabled={Number(deletingId) === Number(document.iddoc)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Eliminar publicación"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-sm italic text-gray-500">
                  No hay publicaciones.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const SportIndividualArea = ({ idevent, idsport }) => {
  const [publicationType, setPublicationType] = useState(
    PUBLICATION_TYPE.PROGRAMACION,
  );
  const [source, setSource] = useState(SOURCE_TYPE.PDF);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [documents, setDocuments] = useState([]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await listIndividualDocuments({ idevent, idsport });
      setDocuments(response);
    } catch (error) {
      console.error("Error loading individual documents", error);
      toast.error("No se pudieron cargar las publicaciones.");
    }
  }, [idevent, idsport]);

  useEffect(() => {
    if (idevent && idsport) fetchDocuments();
  }, [fetchDocuments, idevent, idsport]);

  const resetForm = () => {
    setTitle("");
    setSelectedFile(null);
    setLinkUrl("");
    setFileInputKey((value) => value + 1);
  };

  const handleSourceChange = (nextSource) => {
    setSource(nextSource);
    setSelectedFile(null);
    setLinkUrl("");
    setFileInputKey((value) => value + 1);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();

    if (source === SOURCE_TYPE.PDF) {
      const fileName = String(selectedFile?.name || "").toLowerCase();
      if (!selectedFile || !fileName.endsWith(".pdf")) {
        toast.error("Selecciona un archivo PDF válido.");
        return;
      }
    }

    if (source === SOURCE_TYPE.LINK) {
      if (!trimmedTitle) {
        toast.error("Ingresa un título para el enlace.");
        return;
      }
      if (!isHttpUrl(linkUrl)) {
        toast.error("Ingresa un enlace válido que comience con http:// o https://.");
        return;
      }
    }

    setUploading(true);
    try {
      await createIndividualDocument({
        idevent,
        idsport,
        publicationType,
        source,
        title: trimmedTitle || selectedFile?.name || "",
        file: selectedFile,
        url: linkUrl.trim(),
      });

      toast.success("Publicación agregada correctamente.");
      resetForm();
      await fetchDocuments();
    } catch (error) {
      console.error("Error creating individual publication", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "No se pudo agregar la publicación.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (iddoc) => {
    setDeletingId(iddoc);
    try {
      await deleteIndividualDocument({ iddoc });
      toast.success("Publicación eliminada correctamente.");
      await fetchDocuments();
    } catch (error) {
      console.error("Error deleting individual publication", error);
      toast.error("No se pudo eliminar la publicación.");
    } finally {
      setDeletingId(null);
    }
  };

  const programmingDocuments = useMemo(
    () => documents.filter((document) => Number(document.tipo) === PUBLICATION_TYPE.PROGRAMACION),
    [documents],
  );
  const resultDocuments = useMemo(
    () => documents.filter((document) => Number(document.tipo) === PUBLICATION_TYPE.RESULTADO),
    [documents],
  );

  return (
    <div className="mx-auto w-full space-y-8">
      <section className="w-full rounded-2xl bg-white p-5 shadow-lg sm:p-6">
        <div className="mb-6 flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
            <FaUpload />
          </span>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Publicar información o resultados
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Puedes adjuntar un archivo PDF o publicar un enlace externo.
              {publicationType === PUBLICATION_TYPE.PROGRAMACION
                ? " Lo que publiques en Información también aparece automáticamente en la Información de Hayllis 2.0."
                : ""}
            </p>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <span className="mb-2 block text-sm font-semibold text-gray-700">
              Sección
            </span>
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1.5">
              {[
                [PUBLICATION_TYPE.PROGRAMACION, "Información"],
                [PUBLICATION_TYPE.RESULTADO, "Resultado"],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setPublicationType(value)}
                  className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    publicationType === value
                      ? "bg-white text-indigo-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-2 block text-sm font-semibold text-gray-700">
              Origen
            </span>
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1.5">
              <button
                type="button"
                onClick={() => handleSourceChange(SOURCE_TYPE.PDF)}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                  source === SOURCE_TYPE.PDF
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FaFilePdf /> Archivo PDF
              </button>
              <button
                type="button"
                onClick={() => handleSourceChange(SOURCE_TYPE.LINK)}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                  source === SOURCE_TYPE.LINK
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FaLink /> Enlace
              </button>
            </div>
          </div>

          <label className="grid gap-2 text-sm font-semibold text-gray-700 lg:col-span-2">
            Título {source === SOURCE_TYPE.LINK ? "*" : ""}
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={
                publicationType === PUBLICATION_TYPE.PROGRAMACION
                  ? "Ejemplo: Programación oficial - Día 1"
                  : "Ejemplo: Resultados finales"
              }
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 font-normal text-gray-900 placeholder-gray-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            />
          </label>

          {source === SOURCE_TYPE.PDF ? (
            <label className="grid gap-2 text-sm font-semibold text-gray-700 lg:col-span-2">
              Archivo PDF *
              <input
                key={fileInputKey}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                disabled={uploading}
                className="block w-full rounded-lg border border-gray-300 bg-white text-gray-700 file:mr-4 file:border-0 file:border-r file:border-gray-200 file:bg-indigo-50 file:px-4 file:py-3 file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 focus:outline-none"
              />
            </label>
          ) : (
            <label className="grid gap-2 text-sm font-semibold text-gray-700 lg:col-span-2">
              Enlace *
              <input
                type="url"
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://..."
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 font-normal text-gray-900 placeholder-gray-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
          )}

          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={uploading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <FaUpload />
              {uploading ? "Publicando..." : "Agregar publicación"}
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <PublicationTable
          title="Información"
          documents={programmingDocuments}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
        <PublicationTable
          title="Resultados"
          documents={resultDocuments}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      </div>
    </div>
  );
};

export default SportIndividualArea;
